import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { hasPermission } from "@/types";
import type { AuthUser } from "@/types";
import { StageBadge } from "../common/StageActions";
import { ActivityTimeline } from "../common/ActivityTimeline";
import {
  CandidateInterviews,
  ScheduleInterviewDialog,
} from "./CandidateInterviews";
import { CandidateNotes } from "./CandidateNotes";
import { EndorsementDialog } from "../dialog/EndorsementDialog";
import { CvDialog } from "../dialog/CvDialog";
import { PersonalTab } from "../tab/PersonalTab";
import { SocialTab } from "../tab/SocialTab";
import { EducationTab } from "../tab/EducationTab";
import { ResumeTab } from "../tab/ResumeTab";
import { SalaryTab } from "../tab/SalaryTab";
import { DocumentsTab } from "../tab/DocumentsTab";
import { ContactsTab } from "../tab/ContactsTab";
import type {
  ProfileData,
  Document,
  EmergencyContact,
  Dependent,
} from "../../../lib/profileTypes";

export type Tab =
  | "personal"
  | "social"
  | "education"
  | "resume"
  | "salary"
  | "documents"
  | "contacts"
  | "interviews"
  | "notes"
  | "activity";
export const PROFILE_TABS: Tab[] = [
  "personal",
  "social",
  "education",
  "resume",
  "salary",
  "documents",
  "contacts",
  "interviews",
  "notes",
  "activity",
];

const ALL_TABS: { key: Tab; label: string }[] = [
  { key: "personal", label: "Personal" },
  { key: "social", label: "Social" },
  { key: "education", label: "Education & Work" },
  { key: "resume", label: "Resume" },
  { key: "salary", label: "Salary" },
  { key: "documents", label: "Documents" },
  { key: "contacts", label: "Contacts & Dependents" },
  { key: "interviews", label: "Interviews" },
  { key: "notes", label: "Notes" },
  { key: "activity", label: "Activity" },
];

interface CandidateProfileProps {
  candidateId: number;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  onBack: () => void;
  currentUser?: AuthUser;
  initialDialog?: "cv" | "interview" | "endorse";
  onDialogClose?: () => void;
  onOpenDialog?: (dialog: "cv" | "interview" | "endorse") => void;
}

export function CandidateProfile({
  candidateId,
  activeTab,
  onTabChange,
  onBack,
  currentUser,
  initialDialog,
  onDialogClose,
  onOpenDialog,
}: CandidateProfileProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState<
    "cv" | "interview" | "endorse" | null
  >(initialDialog ?? null);

  useEffect(() => {
    setOpenDialog(initialDialog ?? null);
  }, [initialDialog]);

  const closeDialog = () => {
    setOpenDialog(null);
    onDialogClose?.();
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.candidateProfile(candidateId),
    queryFn: () => api.getCandidateProfile(candidateId),
  });

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.updateCandidateProfile({ candidate_id: candidateId, ...body }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.candidateProfile(candidateId),
      });
      setEditing(false);
    },
  });

  const tabs = useMemo(
    () =>
      ALL_TABS.filter(
        (t) =>
          !currentUser || hasPermission(currentUser, `profile:${t.key}:read`),
      ),
    [currentUser],
  );

  const canWriteTab = (t: Tab) =>
    !currentUser || hasPermission(currentUser, `profile:${t}:write`);

  useEffect(() => {
    if (initialDialog) return;
    if (tabs.length > 0 && !tabs.find((t) => t.key === activeTab)) {
      onTabChange(tabs[0].key);
    }
  }, [tabs, activeTab, onTabChange, initialDialog]);

  if (isLoading)
    return <p className="text-text-secondary">Loading profile...</p>;
  if (!data) return <p className="text-text-secondary">Profile not found</p>;

  const { candidate } = data;
  const profile = (data.profile || {}) as ProfileData;
  const documents = (data.documents || []) as Document[];
  const emergencyContacts = (data.emergency_contacts ||
    []) as EmergencyContact[];
  const dependents = (data.dependents || []) as Dependent[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = candidate as any;
  const tabProps = {
    editing,
    onSave: (d: Record<string, unknown>) => mutation.mutate(d),
    onCancel: () => setEditing(false),
    saving: mutation.isPending,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <button
          onClick={onBack}
          className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-btn text-text-secondary hover:bg-surface-secondary hover:text-near-black"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
        </button>
        {candidate.photo && (
          <img
            src={candidate.photo}
            alt={candidate.name || ""}
            className="mt-0.5 h-14 w-14 shrink-0 rounded-full object-cover shadow-card"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-[28px] font-bold leading-[1.43] text-near-black truncate">
              {candidate.name || "Unknown"}
            </h2>
            <StageBadge stage={candidate.current_stage} />
          </div>
          <p className="text-sm text-text-secondary">
            {candidate.email || ""}{" "}
            {candidate.phone ? `· ${candidate.phone}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button
            onClick={() =>
              onOpenDialog ? onOpenDialog("cv") : setOpenDialog("cv")
            }
            className="flex items-center gap-1.5 rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-surface-secondary hover:text-near-black"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
            View CV
          </button>
          <button
            onClick={() =>
              onOpenDialog
                ? onOpenDialog("interview")
                : setOpenDialog("interview")
            }
            className="flex items-center gap-1.5 rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-surface-secondary hover:text-near-black"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
            Schedule Interview
          </button>
          <button
            onClick={() =>
              onOpenDialog ? onOpenDialog("endorse") : setOpenDialog("endorse")
            }
            className="flex items-center gap-1.5 rounded-btn border border-border-card bg-white px-3 py-1.5 text-xs font-medium text-text-focused transition-colors hover:bg-surface-secondary hover:text-near-black"
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            Endorse
          </button>
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-surface-secondary pb-px">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setEditing(false);
              onTabChange(t.key);
            }}
            className={`shrink-0 px-3 py-2 text-sm font-medium transition-colors ${activeTab === t.key ? "border-b-2 border-near-black text-near-black" : "text-text-secondary hover:text-near-black"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "personal" && (
        <PersonalTab
          profile={profile}
          candidate={candidate}
          {...tabProps}
          onEdit={canWriteTab("personal") ? () => setEditing(true) : undefined}
        />
      )}
      {activeTab === "social" && (
        <SocialTab
          profile={profile}
          {...tabProps}
          onEdit={canWriteTab("social") ? () => setEditing(true) : undefined}
        />
      )}
      {activeTab === "education" && (
        <EducationTab
          profile={profile}
          candidateId={candidateId}
          hasResume={!!c.raw_resume}
          {...tabProps}
          onEdit={canWriteTab("education") ? () => setEditing(true) : undefined}
        />
      )}
      {activeTab === "resume" && (
        <ResumeTab
          candidateId={candidateId}
          hasFile={!!c.resume_pdf_key}
          mime={c.resume_pdf_mime ?? null}
        />
      )}
      {activeTab === "salary" && (
        <SalaryTab
          profile={profile}
          candidateId={candidateId}
          {...tabProps}
          onEdit={canWriteTab("salary") ? () => setEditing(true) : undefined}
        />
      )}
      {activeTab === "documents" && (
        <DocumentsTab
          documents={documents}
          canWrite={canWriteTab("documents")}
          onSave={tabProps.onSave}
          saving={tabProps.saving}
        />
      )}
      {activeTab === "contacts" && (
        <ContactsTab
          contacts={emergencyContacts}
          dependents={dependents}
          characterRefs={profile.character_references || []}
          candidateId={candidateId}
          rawResume={c.raw_resume || ""}
          canWrite={canWriteTab("contacts")}
          onSave={tabProps.onSave}
          saving={tabProps.saving}
        />
      )}
      {activeTab === "interviews" && (
        <div className="rounded-card bg-white p-4 shadow-card">
          <CandidateInterviews candidateId={candidateId} />
        </div>
      )}
      {activeTab === "notes" && currentUser && (
        <div className="rounded-card bg-white p-4 shadow-card">
          <CandidateNotes candidateId={candidateId} currentUser={currentUser} />
        </div>
      )}
      {activeTab === "activity" && (
        <div className="rounded-card bg-white p-4 shadow-card">
          <ActivityTimeline candidateId={candidateId} />
        </div>
      )}

      {openDialog === "cv" && (
        <CvDialog
          candidateId={candidateId}
          candidateName={candidate.name || ""}
          onClose={closeDialog}
        />
      )}
      {openDialog === "interview" && (
        <ScheduleInterviewDialog
          candidateId={candidateId}
          onClose={closeDialog}
          onCreated={closeDialog}
        />
      )}
      {openDialog === "endorse" && (
        <EndorsementDialog
          candidate={c}
          positionTitle={c.position_title || ""}
          currentUser={currentUser}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
